/**
 * Who sits on each counter from the top of the shift, worked out at step 3 of the
 * generate wizard so the Handwritten Ctr picker can say which counters are free and
 * for how long, before the roster is built.
 *
 * It mirrors what the roster builder does with the same inputs (backend/roster_builder.py):
 * each reported officer starts from the roster the PDF prints, an RA clears the slots before
 * it, an RO clears the slots from its close time, excluded ranges are cleared, and a
 * handwritten counter overwrites the top of the shift (GL in the morning: 5 slots; any other
 * morning officer: 2; at night: up to the chosen end time).
 */
import { NUM_SLOTS, hmmmToEndSlot } from '@/config/modeConfig';

export interface VacancyOfficer {
  sn: number;
  name: string;
  /** Internal counter number of their handwritten counter; 0 for none. */
  hwCounter: number;
  /** Exclusive end slot of that handwritten counter. */
  hwEndSlot: number;
  /** Exclusive slot their RA starts them from; 0 for none. */
  raSlot: number;
  /** Slot their RO takes them off from; NUM_SLOTS for none. */
  roSlot: number;
  /** "HHMM-HHMM" ranges they are away. */
  excludeTimes: string[];
}

export interface CounterOccupant {
  sn: number;
  name: string;
  /** What to call them when "SN<sn> <name>" is wrong — an SOS officer on a counter of their own. */
  label?: string;
  /** Sitting there on a handwritten counter rather than the printed roster. */
  handwritten: boolean;
}

export type CounterVacancy =
  /** Nobody on it at shift start; free for `untilSlot` slots (≥ 1, NUM_SLOTS = all shift). */
  | { state: 'free'; untilSlot: number }
  /** Someone on it at shift start, until `untilSlot` (exclusive). */
  | { state: 'taken'; untilSlot: number; occupant: CounterOccupant }
  /** Chops took the counter out of service. */
  | { state: 'blocked' };

/** Slots a handwritten counter covers, as the roster builder counts them. */
export function hwSlots(sn: number, shiftType: 'morning' | 'night', hwEndTime: string, startHour: number): number {
  if (shiftType === 'night') return hwEndTime ? hmmmToEndSlot(hwEndTime, startHour) : 6;
  return sn % 4 === 0 ? 5 : 2;
}

/**
 * Each reported officer's counter per slot (0 = none), and whether that slot is their
 * handwritten counter. `template` is the PDF's printed roster per S/N, or null when
 * there is none — then only the handwritten counters are known.
 */
function schedules(
  officers: VacancyOfficer[],
  template: Record<string, number[]> | null,
  startHour: number,
): { sn: number; name: string; slots: number[]; hwUntil: number }[] {
  return officers.map((o) => {
    const printed = template?.[o.sn] ?? template?.[String(o.sn)] ?? [];
    const slots = Array.from({ length: NUM_SLOTS }, (_, i) => (printed[i] ?? 0) > 0 ? printed[i] : 0);
    for (let i = 0; i < Math.min(o.raSlot, NUM_SLOTS); i++) slots[i] = 0;
    for (let i = Math.max(o.roSlot, 0); i < NUM_SLOTS; i++) slots[i] = 0;
    for (const r of o.excludeTimes) {
      const m = /^(\d{4})-(\d{4})$/.exec(r);
      if (!m) continue;
      const s = hmmmToEndSlot(m[1], startHour);
      const e = hmmmToEndSlot(m[2], startHour);
      for (let i = s; i < e; i++) slots[i] = 0;
    }
    const hwUntil = o.hwCounter > 0 ? Math.min(o.hwEndSlot, NUM_SLOTS) : 0;
    for (let i = 0; i < hwUntil; i++) slots[i] = o.hwCounter;
    return { sn: o.sn, name: o.name, slots, hwUntil };
  });
}

/** Someone who is not a main officer on counters across the shift — an SOS officer from the SOS table. */
export interface ExtraOccupant {
  name: string;
  /** Counter per slot, 0 for none. */
  slots: number[];
}

/**
 * Every counter's state at `slot`, for the picker of officer `forSn` — whose own row is left out, since
 * picking a counter for them moves them off their printed one. Free means free from `slot` for `untilSlot`
 * - `slot` slots; taken means someone is on it at `slot`, until `untilSlot` (both are slot numbers from the
 * start of the shift). `extras` are other occupants of the board, e.g. SOS officers given a counter.
 */
export function counterVacanciesAt(
  slot: number,
  counters: number[],
  officers: VacancyOfficer[],
  template: Record<string, number[]> | null,
  startHour: number,
  forSn: number,
  blocked: number[] = [],
  extras: ExtraOccupant[] = [],
): Map<number, CounterVacancy> {
  const others: { sn: number; name: string; slots: number[]; hwUntil: number; label?: string }[] = [
    ...schedules(officers.filter((o) => o.sn !== forSn), template, startHour),
    ...extras.map((x) => ({ sn: 0, name: x.name, slots: x.slots, hwUntil: 0, label: `SOS ${x.name || "officer"}` })),
  ];
  const at = Math.min(Math.max(slot, 0), NUM_SLOTS - 1);
  const out = new Map<number, CounterVacancy>();
  for (const c of counters) {
    if (blocked.includes(c)) { out.set(c, { state: 'blocked' }); continue; }
    const first = others.find((s) => s.slots[at] === c);
    if (first) {
      let until = at + 1;
      while (until < NUM_SLOTS && first.slots[until] === c) until += 1;
      out.set(c, {
        state: 'taken',
        untilSlot: until,
        occupant: { sn: first.sn, name: first.name, handwritten: first.hwUntil > 0, label: first.label },
      });
      continue;
    }
    let until = at + 1;
    while (until < NUM_SLOTS && !others.some((s) => s.slots[until] === c)) until += 1;
    out.set(c, { state: 'free', untilSlot: until });
  }
  return out;
}

/** Every counter's state at shift start (see counterVacanciesAt). */
export function counterVacancies(
  counters: number[],
  officers: VacancyOfficer[],
  template: Record<string, number[]> | null,
  startHour: number,
  forSn: number,
  blocked: number[] = [],
): Map<number, CounterVacancy> {
  return counterVacanciesAt(0, counters, officers, template, startHour, forSn, blocked);
}

/** The picker's line for a counter: free until when, or who holds it until when. */
export function vacancyText(v: CounterVacancy | undefined, slotTime: (slot: number) => string, totalSlots: number): string {
  if (!v) return "";
  if (v.state === "blocked") return "Out of service";
  if (v.state === "free") return v.untilSlot >= totalSlots ? "Free whole shift" : `Free until ${slotTime(v.untilSlot)}`;
  const who = v.occupant.label ?? `SN${v.occupant.sn}${v.occupant.name ? ` ${v.occupant.name}` : ""}`;
  return `${who}${v.occupant.handwritten ? " (handwritten)" : ""} until ${slotTime(v.untilSlot)}`;
}
